import React, { createRef } from 'react';
import { renderToString } from 'react-dom/server';
import { textRenderer } from 'handsontable/renderers';
import { BaseEditorComponent } from '@handsontable/react';
import { Chip } from '@mui/material';
import { withStyles } from '@mui/styles';
import { ix } from '../../../../../utils/ix';
import { Icon } from '../../../../atoms';
import { SelectDropdownField } from '../../../formFields';
import { isJsonString } from '../../Spreadsheet.utils';
import styles, { useStyles } from './SpreadsheetInputSelect.styles';
import { Theme } from '../../../../electrons';
function ChipRenderer(label) {
    const classes = useStyles();
    return renderToString(React.createElement(Theme, null,
        React.createElement(Chip, { label: label, className: classes.chip })));
}
function ExpandIconRenderer() {
    return renderToString(React.createElement(Icon, { className: "select-expand-icon" }, "expand_more"));
}
export function useSpreadsheetInputSelectRenderer() {
    const tempLabel = 'string-to-replace';
    const chipHtml = ChipRenderer(tempLabel);
    const expandHtml = ExpandIconRenderer();
    return (instance, td, row, col, prop, value, cellProperties) => {
        textRenderer(instance, td, row, col, prop, value, cellProperties);
        if (!td.classList.contains('htMiddle')) {
            td.classList.add('htMiddle');
        }
        if (!td.classList.contains('htSelect')) {
            td.classList.add('htSelect');
        }
        if (cellProperties.readOnly) {
            td.classList.add('htDimmed');
        }
        if (cellProperties.multiple) {
            const options = JSON.parse(value || '[]');
            value = options
                .map(({ label }) => chipHtml.replace(tempLabel, label))
                .join('');
        }
        else {
            const option = JSON.parse(value || '{}');
            value = option === null || option === void 0 ? void 0 : option.label;
        }
        value = value || '';
        td.innerHTML = `<div><span title="">${value}</span>${expandHtml}</div>`;
        return td;
    };
}
/* Editors need to be class-components as they need to extend base editor class */
export const SpreadsheetInputSelectEditor = withStyles(styles)(class SpreadsheetInputSelectEditor extends BaseEditorComponent {
    constructor(props) {
        super(props);
        this.options = [];
        this.containerRef = createRef();
        this.state = { value: '' };
    }
    open() {
        var _a;
        if (!this.containerRef.current)
            return;
        this.containerRef.current.style.display = 'block';
        super.beginEditing();
        (_a = this.containerRef.current.querySelector('input')) === null || _a === void 0 ? void 0 : _a.focus();
    }
    close() {
        if (!this.containerRef.current)
            return;
        this.containerRef.current.style.display = 'none';
    }
    focus() {
        var _a;
        if (!this.containerRef.current)
            return;
        (_a = this.containerRef.current.querySelector('input')) === null || _a === void 0 ? void 0 : _a.focus();
    }
    prepare(row, col, prop, container, originalValue, cellProperties) {
        super.prepare(row, col, prop, container, originalValue, cellProperties);
        if (!this.containerRef.current)
            return;
        const containerPos = container.getBoundingClientRect();
        const insetFocusOutlineWidth = 1;
        const top = containerPos.top + window.scrollY + insetFocusOutlineWidth;
        const left = containerPos.left + window.scrollX + insetFocusOutlineWidth;
        this.containerRef.current.style.top = `${top}px`;
        this.containerRef.current.style.left = `${left}px`;
        this.containerRef.current.style.width = `${containerPos.width - 3}px`;
        this.containerRef.current.style.height = `${containerPos.height - 3}px`;
        this.options = this.cellProperties.options;
    }
    setValue(value, callback) {
        this.setState(() => {
            return { value };
        }, callback);
    }
    getValue() {
        return this.state.value;
    }
    onItemClicked(options) {
        const stringified = JSON.stringify(options);
        this.setState(() => {
            return { value: stringified };
        }, () => {
            this.finishEditing();
        });
    }
    getCurrentlySelectedOption() {
        if (!this.state.value || !isJsonString(this.state.value))
            return null;
        const value = JSON.parse(this.state.value);
        const { creatable } = this.props;
        if (Array.isArray(value)) {
            if (creatable)
                return value;
            const values = value.map((v) => v.value);
            return this.options.filter((o) => values.includes(o.value));
        }
        return this.options.find((o) => o.value === value) || null;
    }
    render() {
        /*
         * Type casting needed because BaseEditorComponent has incorrect Props
         * types: uses union (|) operator on props with HotEditorProps instead of
         * intersection (&), resulting in TS failing because in union all types
         * need referenced property, whereas in intersection at least one.
         *
         * TODO: Raise bug with handsontable once we have license bought.
         */
        const { classes, multiple, creatable, columnName, renderOption, virtualized, virtualizationConfig, } = this.props;
        return (React.createElement("div", Object.assign({ ref: this.containerRef, className: classes.container }, (columnName
            ? { id: ix('spreadsheet-input-select', columnName) }
            : {})),
            React.createElement(SelectDropdownField, { options: this.options, className: classes.select, defaultOpen: true, multiple: multiple, allowCreate: creatable || false, value: this.getCurrentlySelectedOption(), maxDisplayedSelectedOptions: 2, onChange: (option) => {
                    this.onItemClicked(option);
                }, renderOption: renderOption, virtualized: virtualized, virtualizationConfig: virtualizationConfig })));
    }
});
//# sourceMappingURL=SpreadsheetInputSelect.js.map